import { atom, useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import Constants from 'constants/index';

import type {
  SearchDocument,
  SearchResponse,
} from '@/_generated/api-clients/xp';

export interface SearchState {
  currentPage: number;
  hasNextPage: boolean;
  numFound: number;
  result: SearchResponse | null;
  pageSize: number;
  searchResults: SearchDocument[];
  showingResults: number | null;
  terms: string[];
  query: string;
  queryId: string;
}

const DEFAULT_STATE: SearchState = {
  currentPage: 1,
  hasNextPage: false,
  numFound: 0,
  pageSize: Constants.DEFAULT_PAGING_SIZE,
  result: null,
  searchResults: [],
  showingResults: 0,
  terms: [],
  query: '',
  queryId: '',
};

export const searchBaseAtom = atomWithStorage('app.search', DEFAULT_STATE);

const base = searchBaseAtom;

export const searchAtom = atom(
  get => get(base),
  (get, set, patch: Partial<SearchState>) => {
    set(base, { ...get(base), ...patch });
  },
);

type Action =
  | { type: 'addSearchResults'; payload: SearchDocument[] }
  | { type: 'removeSearchTerm'; payload: string }
  | { type: 'resetSearchState' }
  | { type: 'saveSearchTerm'; payload: string };

export const searchDispatchAtom = atom(
  get => get(base),
  (get, set, action: Action) => {
    const prev = get(base);
    switch (action.type) {
      case 'addSearchResults':
        set(base, {
          ...prev,
          searchResults: [...prev.searchResults, ...action.payload],
        });
        return;
      case 'removeSearchTerm':
        set(base, {
          ...prev,
          terms: prev.terms.filter(term => term !== action.payload),
        });
        return;
      case 'resetSearchState':
        set(base, {
          ...DEFAULT_STATE,
        });
        return;
      case 'saveSearchTerm': {
        const termExistsIndex = prev.terms.findIndex(
          term => action.payload === term,
        );
        if (termExistsIndex >= 0) {
          if (termExistsIndex === 0) {
            return;
          } else {
            const termsWithExistingTermRemoved = [...prev.terms].filter(
              (_term, idx) => idx !== termExistsIndex,
            );
            set(base, {
              ...prev,
              terms: [action.payload, ...termsWithExistingTermRemoved],
            });
          }
        } else {
          set(base, {
            ...prev,
            terms: [action.payload, ...prev.terms].slice(0, 5),
          });
        }
        return;
      }
    }
  },
);

export const useSearchDispatch = () => {
  const setDispatch = useSetAtom(searchDispatchAtom);
  return {
    saveTerm: (term: string) => {
      setDispatch({ type: 'saveSearchTerm', payload: term });
    },
    removeTerm: (term: string) => {
      setDispatch({ type: 'removeSearchTerm', payload: term });
    },
    addResults: (results: SearchDocument[]) => {
      setDispatch({ type: 'addSearchResults', payload: results });
    },
    reset: () => {
      setDispatch({ type: 'resetSearchState' });
    },
  };
};
